import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { ChevronLeft, ChevronRight, ArrowRight, Check, Pause, Play, X } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STEP_MS = 9000;

export default function TourCarousel({ open, modules = [], onClose, onComplete }) {
  const steps = modules.filter((m) => m.has_media);
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const timer = useRef(null);
  const last = steps.length - 1;

  useEffect(() => { if (open) { setIdx(0); setPaused(false); } }, [open]);

  const next = useCallback(() => setIdx((i) => Math.min(last, i + 1)), [last]);

  // auto-advance with a progress bar
  useEffect(() => {
    if (!open || paused || !steps.length) return;
    setProgress(0);
    const start = Date.now();
    timer.current = setInterval(() => {
      const p = Math.min(100, ((Date.now() - start) / STEP_MS) * 100);
      setProgress(p);
      if (p >= 100) {
        clearInterval(timer.current);
        if (idx < last) next();
        else setPaused(true);
      }
    }, 100);
    return () => clearInterval(timer.current);
  }, [open, paused, idx, last, steps.length, next]);

  if (!open || !steps.length) return null;
  const m = steps[idx];
  const url = `${BACKEND}/api/training/media/${m.media_path}`;
  const isVideo = (m.media_type || "").startsWith("video");

  const goModule = () => { onClose && onClose(); navigate(m.route); };
  const finish = () => { onComplete && onComplete(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="tour-carousel">
        {/* progress segments */}
        <div className="flex gap-1 px-4 pt-4">
          {steps.map((_, i) => (
            <div key={i} className="h-1 flex-1 rounded-full bg-border overflow-hidden">
              <div className="h-full bg-accent transition-[width] duration-100" style={{ width: i < idx ? "100%" : i === idx ? `${progress}%` : "0%" }} />
            </div>
          ))}
        </div>

        <div className="px-5 pt-3 flex items-center justify-between">
          <div className="label-eyebrow text-[10px]">Guided tour · {idx + 1} of {steps.length}</div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setPaused((p) => !p)} data-testid="tour-pause-btn">
              {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            </Button>
            <Button size="sm" variant="ghost" onClick={finish} data-testid="tour-skip-btn"><X className="h-4 w-4" /></Button>
          </div>
        </div>

        <div className="px-5 pb-2">
          <h2 className="font-display text-xl font-bold tracking-tight" data-testid="tour-step-title">{m.label}</h2>
          <p className="text-sm text-muted-foreground mt-0.5 min-h-[2.5rem]">{m.desc}</p>
        </div>

        <div className="px-5">
          <div className="rounded-md overflow-hidden border border-border bg-black/30">
            {isVideo
              ? <video key={m.key} src={url} autoPlay muted loop playsInline className="w-full block" data-testid="tour-media" />
              : <img key={m.key} src={url} alt={`${m.label} walkthrough`} className="w-full block" data-testid="tour-media" />}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 p-5">
          <Button variant="outline" size="sm" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))} data-testid="tour-prev-btn">
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
          <Button variant="ghost" size="sm" onClick={goModule} data-testid="tour-open-module-btn">
            Open this module <ArrowRight className="h-3.5 w-3.5" />
          </Button>
          {idx < last ? (
            <Button variant="primary" size="sm" onClick={next} data-testid="tour-next-btn">Next <ChevronRight className="h-4 w-4" /></Button>
          ) : (
            <Button variant="primary" size="sm" onClick={finish} data-testid="tour-finish-btn"><Check className="h-4 w-4" /> Finish</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
